// frontend_MainDashboard/src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';

// --- IMPORT FIREBASE ---
import { User, onIdTokenChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail as firebaseSendPasswordResetEmail, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../firebase';  // Assuming firebase.ts is in the parent directory
import Spinner from '../components/UI/Spinner';

// Define what you want to store about the Firebase user locally if needed
// (This is mainly for localStorage, the context will hold the full Firebase user object)
interface StoredUserMinimal {
  uid: string;
  email: string | null;
  // You could add displayName here if you consistently set it via Firebase Auth profile
}

// Define the shape of your Auth Context
interface AuthContextType {
  currentUser: User | null; // Store the full Firebase User object
  idToken: string | null;            // Store the Firebase ID token
  isLoading: boolean;                // For async operations and initial auth state check
  error: string | null;              // For displaying auth-related errors
  loginWithEmail: (email: string, password_unused: string) => Promise<void>; // Login function
  signupWithEmail: (email: string, password: string, displayName?: string) => Promise<User>; // Signup function
  logout: () => Promise<void>;        // Logout function
  sendPasswordResetEmail: (email: string) => Promise<void>; // Password reset function
  getValidIdToken: (forceRefresh?: boolean) => Promise<string | null>;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create the provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start true to check initial auth state
  const [error, setError] = useState<string | null>(null);

  // Function to refresh the ID token
  const refreshIdToken = useCallback(async (user: User): Promise<string | null> => {
    try {
      const token = await user.getIdToken(true); // Force refresh
      setIdToken(token);
      console.log("AuthContext: ID token refreshed successfully");
      return token;
    } catch (tokenError: any) {
      console.error("AuthContext: Error refreshing ID token:", tokenError);
      setError("Session expired. Please log in again.");
      setIdToken(null);
      localStorage.removeItem('authUserMinimal');
      return null;
    }
  }, []);

  // Subscribe to ID token changes so any refresh updates idToken in context,
  // preventing consumers from using a stale token during bootstrap.
  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      console.log("AuthContext: Auth state changed (onIdTokenChanged), user:", user?.email || "null");
      setCurrentUser(user);
      if (user) {
        try {
          // Normal getIdToken picks up refreshed tokens. No forced refresh here.
          const token = await user.getIdToken();
          setIdToken(token);

          const minimalUser: StoredUserMinimal = { uid: user.uid, email: user.email };
          localStorage.setItem('authUserMinimal', JSON.stringify(minimalUser));
          console.log("AuthContext: User authenticated, setting isLoading to false");
        } catch (tokenError: any) {
          console.error("AuthContext: Error getting ID token:", tokenError);
          setError("Failed to retrieve session token. Please try logging in again.");
          setIdToken(null);
          localStorage.removeItem('authUserMinimal');
        }
      } else {
        // No user is signed in
        setIdToken(null);
        localStorage.removeItem('authUserMinimal');
        console.log("AuthContext: No user, setting isLoading to false");
      }
      setIsLoading(false);
    });

    return unsubscribe; // Cleanup subscription on component unmount
  }, []);

  // Auto-refresh token every 50 minutes (before 1-hour expiration)
  useEffect(() => {
    if (!currentUser) return;

    console.log("AuthContext: Setting up token refresh interval");
    const refreshInterval = setInterval(async () => {
      console.log("AuthContext: Auto-refreshing ID token");
      await refreshIdToken(currentUser);
    }, 50 * 60 * 1000); // 50 minutes

    return () => {
      console.log("AuthContext: Clearing token refresh interval");
      clearInterval(refreshInterval);
    };
  }, [currentUser, refreshIdToken]);

  const loginWithEmail = useCallback(async (email: string, password_unused: string): Promise<void> => {
    console.log("AuthContext: loginWithEmail called with email:", email);
    setIsLoading(true);
    setError(null);
    try {
      console.log("AuthContext: Attempting Firebase signIn...");
      const userCredential = await signInWithEmailAndPassword(auth, email, password_unused);
      console.log("AuthContext: Firebase signIn successful:", userCredential.user?.email);
      // The onAuthStateChanged listener (above) will automatically handle setting
      // currentUser and idToken once Firebase confirms the sign-in.
      // We can also proactively set idToken here if desired for faster UI updates.
      if (userCredential.user) {
        const token = await userCredential.user.getIdToken(true);
        setIdToken(token); // Proactively set for immediate use
        setCurrentUser(userCredential.user); // Proactively set the user
        console.log("AuthContext: ID token obtained and user set");
      }
      setIsLoading(false); // Explicitly set loading to false after successful login
    } catch (err: any) {
      console.error("AuthContext: Firebase login error:", err);
      // Firebase provides error codes and messages, e.g., err.code, err.message
      let friendlyMessage = "Login failed. Please check your email and password.";
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        friendlyMessage = "Invalid email or password.";
      } else if (err.code === 'auth/invalid-email') {
        friendlyMessage = "The email address is not valid.";
      } // Add more specific Firebase error codes as needed
      setError(friendlyMessage);
      setIsLoading(false); // Ensure loading is false on error
      throw err; // Re-throw the error so the calling component (LoginScreen) can also catch it if needed
    }
  }, []);

  const signupWithEmail = useCallback(async (email: string, password: string, displayName?: string): Promise<User> => {
    console.log("AuthContext: signupWithEmail called with email:", email);
    setIsLoading(true);
    setError(null);
    try {
      console.log("AuthContext: Attempting Firebase createUser...");
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log("AuthContext: Firebase createUser successful:", userCredential.user?.email);
      
      // Set display name if provided
      if (displayName && userCredential.user) {
        await updateProfile(userCredential.user, { displayName });
        console.log("AuthContext: Display name set to:", displayName);
      }
      
      // Proactively set user and token
      if (userCredential.user) {
        const token = await userCredential.user.getIdToken(true);
        setIdToken(token);
        setCurrentUser(userCredential.user);
        console.log("AuthContext: ID token obtained and user set after signup");
      }
      
      setIsLoading(false);
      return userCredential.user;
    } catch (err: any) {
      console.error("AuthContext: Firebase signup error:", err);
      let friendlyMessage = "Signup failed. Please try again.";
      if (err.code === 'auth/email-already-in-use') {
        friendlyMessage = "An account with this email already exists. Please sign in instead.";
      } else if (err.code === 'auth/invalid-email') {
        friendlyMessage = "The email address is not valid.";
      } else if (err.code === 'auth/weak-password') {
        friendlyMessage = "Password is too weak. Please use at least 6 characters.";
      }
      setError(friendlyMessage);
      setIsLoading(false);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signOut(auth);
      // The onAuthStateChanged listener will set currentUser and idToken to null.
      // localStorage.removeItem('authUserMinimal'); // Listener also handles this
    } catch (err: any) {
      console.error("AuthContext: Firebase logout error:", err);
      setError(err.message || "Logout failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendPasswordResetEmail = useCallback(async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await firebaseSendPasswordResetEmail(auth, email);
      // Optionally, set a success message here if you want to manage it via context
      // For now, LoginScreen will handle displaying success/info messages.
    } catch (err: any) {
      console.error("AuthContext: Firebase password reset error:", err);
      let friendlyMessage = "Failed to send password reset email. Please try again.";
      if (err.code === 'auth/user-not-found') {
        // It's often better not to reveal if an email exists or not for security reasons.
        // So, a generic message might be preferable.
        friendlyMessage = "If an account exists for this email, a reset link has been sent.";
      } else if (err.code === 'auth/invalid-email') {
        friendlyMessage = "The email address is not valid.";
      }
      setError(friendlyMessage);
      throw err; // Re-throw so the calling component can also catch it
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getValidIdToken = useCallback(async (forceRefresh: boolean = false): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) {
      setIdToken(null);
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      setIdToken(token);
      return token;
    } catch (tokenError) {
      console.error("AuthContext: Error getting ID token, attempting forced refresh.", tokenError);
      return await refreshIdToken(user);
    }
  }, [refreshIdToken]);

  // This is a simple loading state for the initial auth check.
  // Show a loading spinner while checking initial auth state
  if (isLoading && !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ currentUser, idToken, isLoading, error, loginWithEmail, signupWithEmail, logout, sendPasswordResetEmail, getValidIdToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider. Make sure your component is wrapped in AuthProvider.');
  }
  return context;
};